import React, { useState, useEffect, useRef } from 'react';

export interface DropdownItem {
  _id: string;
  name: string;
  [key: string]: any;
}

interface SearchableDropdownProps {
  items: DropdownItem[];
  value: string;
  onChange: (itemId: string, itemName?: string) => void;
  onAddNewItem?: (name: string) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  allowAddNew?: boolean;
  addNewLabel?: string;
  noItemsMessage?: string;
  searchProperty?: string; // Which property to search on (defaults to 'name')
  displayProperty?: string; // Which property to display (defaults to 'name')
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  items,
  value,
  onChange,
  onAddNewItem,
  error,
  disabled = false,
  placeholder = 'Search or select item...',
  allowAddNew = false,
  addNewLabel = 'Add',
  noItemsMessage = 'No items available',
  searchProperty = 'name',
  displayProperty = 'name',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter items based on search term
  const filteredItems = items.filter((item) =>
    item[searchProperty]?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Check if search term has exact match
  const hasExactMatch = filteredItems.some(
    (item) => item[searchProperty]?.toString().toLowerCase() === searchTerm.toLowerCase(),
  );

  // Get display value for input
  const getDisplayValue = () => {
    if (value) {
      const selectedItem = items.find((item) => item._id === value);
      return selectedItem?.[displayProperty]?.toString() || '';
    }
    return searchTerm;
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selection if input doesn't match any item
    if (value) {
      const selectedItem = items.find((item) => item._id === value);
      if (selectedItem && selectedItem[displayProperty]?.toString() !== newValue) {
        onChange('');
      }
    }
  };

  // Handle item selection
  const handleItemSelect = (item: DropdownItem) => {
    onChange(item._id, item[displayProperty]?.toString());
    setSearchTerm(item[displayProperty]?.toString() || '');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Handle add new item
  const handleAddNewItem = () => {
    if (searchTerm.trim() && !hasExactMatch && onAddNewItem) {
      onAddNewItem(searchTerm.trim());
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    const totalItems = filteredItems.length + (searchTerm && !hasExactMatch && allowAddNew && onAddNewItem ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredItems.length) {
            handleItemSelect(filteredItems[highlightedIndex]);
          } else {
            handleAddNewItem();
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update search term when value prop changes externally
  useEffect(() => {
    if (value) {
      const selectedItem = items.find((item) => item._id === value);
      if (selectedItem && searchTerm !== selectedItem[displayProperty]?.toString()) {
        setSearchTerm(selectedItem[displayProperty]?.toString() || '');
      }
    } else if (!isOpen) {
      setSearchTerm('');
    }
  }, [value, items, isOpen, searchTerm, displayProperty]);

  return (
    <div ref={dropdownRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={getDisplayValue()}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded focus:outline-none ${
          error ? 'border-red-500' : 'border-gray-300 focus:ring-2 focus:ring-green-500'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredItems.length === 0 && !searchTerm ? (
            <div className="px-3 py-2 text-gray-500 text-sm">{noItemsMessage}</div>
          ) : (
            <>
              {filteredItems.map((item, index) => (
                <div
                  key={item._id}
                  onClick={() => handleItemSelect(item)}
                  className={`px-3 py-2 cursor-pointer text-sm ${
                    index === highlightedIndex ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100'
                  }`}
                >
                  {item[displayProperty]?.toString()}
                </div>
              ))}

              {searchTerm && !hasExactMatch && allowAddNew && onAddNewItem && (
                <div
                  onClick={handleAddNewItem}
                  className={`px-3 py-2 cursor-pointer text-sm border-t border-gray-200 ${
                    filteredItems.length === highlightedIndex ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100'
                  }`}
                >
                  <span className=" font-medium">
                    + {addNewLabel} "{searchTerm}"
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default SearchableDropdown;
